import { createFileRoute, redirect } from "@tanstack/react-router";
import { authClient } from "@/lib/auth";
import { useHealth, useConfig } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Shield, Activity, Clock } from "lucide-react";

export const Route = createFileRoute("/")({
  component: DashboardPage,
  beforeLoad: async () => {
    const { data } = await authClient.getSession();
    if (!data) {
      throw redirect({ to: "/login" });
    }
  },
});

function DashboardPage() {
  const { data: health } = useHealth();
  const { data: config } = useConfig();

  const stats = [
    {
      title: "Service",
      value: health?.service ?? "—",
      description: "Auth service name",
      icon: Shield,
    },
    {
      title: "Runtime",
      value: health?.runtime ?? "—",
      description: "Deployment target",
      icon: Activity,
    },
    {
      title: "Trusted Origins",
      value: config?.config.trustedOrigins.length ?? "—",
      description: "Allowed client origins",
      icon: Users,
    },
    {
      title: "Last Check",
      value: health?.now ? new Date(health.now).toLocaleTimeString() : "—",
      description: "Health check timestamp",
      icon: Clock,
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground">Overview of your auth service.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Card key={stat.title}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
              <stat.icon className="size-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value}</div>
              <p className="text-xs text-muted-foreground">{stat.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Endpoints</CardTitle>
            <CardDescription>Available API transports</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              <li className="flex justify-between rounded-md bg-muted px-3 py-2">
                <span className="font-medium">Better Auth REST</span>
                <span className="text-muted-foreground">{config?.config.transports.betterAuthRestPath ?? "—"}</span>
              </li>
              <li className="flex justify-between rounded-md bg-muted px-3 py-2">
                <span className="font-medium">REST API</span>
                <span className="text-muted-foreground">{config?.config.transports.restPath ?? "—"}</span>
              </li>
              <li className="flex justify-between rounded-md bg-muted px-3 py-2">
                <span className="font-medium">tRPC</span>
                <span className="text-muted-foreground">{config?.config.transports.trpcPath ?? "—"}</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Allowed Hosts</CardTitle>
            <CardDescription>Hosts permitted by the auth service</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              {config?.config.allowedHosts.map((host) => (
                <li key={host} className="rounded-md bg-muted px-3 py-1.5 font-mono text-xs">
                  {host}
                </li>
              )) ?? <li className="text-muted-foreground">Loading…</li>}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
